import type { PromoCode } from '@/types';

/**
 * Redemption helpers.
 *
 * Codes created before multi-use shipped come back without `maxRedemptions`,
 * `redemptionCount` or the server-computed `remainingRedemptions`. An absent
 * cap means single use — which is NOT the same as `null`, which means
 * unlimited. Everything here keeps those two apart.
 */

/** `null` = unlimited, otherwise the redemption cap. */
export function promoCap(promo: PromoCode): number | null {
    if (promo.isUnlimited) return null;
    if (promo.maxRedemptions === undefined) return 1; // legacy single-use row
    return promo.maxRedemptions;
}

export function promoUsed(promo: PromoCode): number {
    if (typeof promo.redemptionCount === 'number') return promo.redemptionCount;
    // Legacy rows only carry status; a 'used' one was consumed exactly once.
    return promo.status === 'used' ? 1 : 0;
}

/** `null` when unlimited. */
export function promoRemaining(promo: PromoCode): number | null {
    if (typeof promo.remainingRedemptions === 'number') return promo.remainingRedemptions;
    const cap = promoCap(promo);
    return cap === null ? null : Math.max(0, cap - promoUsed(promo));
}

/** Compact table cell, e.g. `3 / 50`, `12 / ∞`, `1 / 1`. */
export function usageLabel(promo: PromoCode): string {
    const cap = promoCap(promo);
    return `${promoUsed(promo)} / ${cap === null ? '∞' : cap}`;
}

/** True when a capped code has no redemptions left. */
export function isExhausted(promo: PromoCode): boolean {
    const remaining = promoRemaining(promo);
    return remaining !== null && remaining <= 0;
}
